// Per-marc state machine + animator. Owns a DetectionGate (private
// member) plus the celebration timer and shown target picker — this is
// the engine that drives one marc's play through to allDone. The outer
// SpeedrunEngine sits above and owns the multi-marc round (lineup,
// currentIndex). Lives outside React; the binding hook (useMarcEngine)
// handles the React-side glue.
//
// State machine (all owned here, none in React):
//   regions       — latest scoring output, fed in via update()
//   shownId       — which region the panel is asking the player to draw
//   celebrating   — true during the post-completion animation window
//   celebrationTimer — clears on dispose / reset
//
// Target picker rules:
//   - prefer regions that have never been committed
//   - then deficient regions (committed but raw fell back below threshold)
//   - else null (allDone)
//
// Transitions:
//   - update() pushes new score result into the gate, then calls
//     _maybeAdvanceShown which either kicks celebration (if the current
//     shown just satisfied) or syncs shownId to the picker's choice.
//   - When the gate emits a transition, the same _maybeAdvanceShown
//     fires.
//   - Celebration timer resolution: clears celebrating, advances shown,
//     emits.
//
// Side-effect callbacks (onTargetChange, onCelebrationStart,
// onCelebrationEnd) fire at the exact moments those events
// conceptually happen — when shownId swaps, when the celebration
// timer kicks off, or when it resolves. The hook passes setters at
// construction time; the engine still doesn't import React, it just
// invokes plain functions. Snapshot subscribers are for things React
// renders (panel chrome); event callbacks are for things React mutates
// (palette / tool state) or out-of-band side effects (sound).
import DetectionGate from "./detectionGate";

const NOOP = () => {};

export default class MarcEngine {
  constructor({
    celebrationMs,
    onTargetChange,
    onCelebrationStart,
    onCelebrationEnd,
  }) {
    this.gate = new DetectionGate();
    this.celebrationMs = celebrationMs;
    this.onTargetChange = onTargetChange ?? NOOP;
    this.onCelebrationStart = onCelebrationStart ?? NOOP;
    this.onCelebrationEnd = onCelebrationEnd ?? NOOP;
    this.regions = [];
    this.shownId = null;
    this.celebrating = false;
    this.celebrationTimer = null;
    this.barEl = null;
    this.listeners = new Set();
    this._unsubGate = this.gate.subscribe(() => this._onGateTransition());
  }

  update({ regions, lastTool, fillMs, strokeMs, celebrationMs }) {
    this.regions = regions;
    if (celebrationMs != null) this.celebrationMs = celebrationMs;
    this.gate.update({ regions, lastTool, fillMs, strokeMs });
    this._maybeAdvanceShown();
    this._emit();
  }

  bindBar(el) {
    this.barEl = el;
    this.gate.bindBar(el, this.shownId);
  }

  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  // Read-only snapshot for the panel. Builds a fresh object each call —
  // callers either store it in React state on emit or read it on demand.
  getSnapshot() {
    const shown =
      this.shownId != null
        ? this.regions.find((r) => r.id === this.shownId)
        : null;
    const realNext = this._pickNext();
    return {
      shownId: this.shownId,
      shownLabel: shown?.label ?? null,
      shownColorIndex: shown?.colorIndex ?? null,
      // ScorePanel reads this to swap title copy + bar color when the
      // current target is overdrawn. Sourced directly from scoring's
      // group-level overdraw flag.
      shownOverdrawn: !!shown?.overdrawn,
      // Full region from scoring (or null) — DebugScoringPanel and the
      // canvas mask overlay consume this. Includes pixelCount, hits,
      // required, score, assignedSize, overdrawAllowed, mode, and a
      // subRegions breakdown.
      shown: shown ?? null,
      incomingLabel: this.celebrating && realNext ? realNext.label : null,
      celebrating: this.celebrating,
      hasLabeled: this.regions.length > 0,
      allDone:
        this.regions.length > 0 && realNext === null && !this.celebrating,
      regions: this.regions.map((r) => {
        const s = this.gate.getRegionState(r.id);
        return {
          id: r.id,
          label: r.label,
          detected: s.satisfied,
          committed: s.committed,
          deficient: s.deficient,
        };
      }),
    };
  }

  // Clears all per-marc state for a fresh marc. Keeps listeners + bar
  // binding so the engine can be re-fed without rebinding.
  reset() {
    if (this.celebrationTimer != null) clearTimeout(this.celebrationTimer);
    this.celebrationTimer = null;
    this.celebrating = false;
    this.shownId = null;
    this.regions = [];
    this.gate.reset();
    this._emit();
  }

  dispose() {
    if (this.celebrationTimer != null) clearTimeout(this.celebrationTimer);
    this.celebrationTimer = null;
    this._unsubGate();
    this.gate.dispose();
    this.listeners.clear();
    this.barEl = null;
  }

  _pickNext() {
    return (
      this.regions.find((r) => !this.gate.getRegionState(r.id).committed) ??
      this.regions.find((r) => !this.gate.getRegionState(r.id).satisfied) ??
      null
    );
  }

  _maybeAdvanceShown() {
    if (this.celebrating) return;
    const realNext = this._pickNext();
    const realNextId = realNext?.id ?? null;
    if (this.shownId === realNextId) return;
    if (this.shownId != null) {
      const shownState = this.gate.getRegionState(this.shownId);
      if (shownState.satisfied) {
        this._startCelebration();
        return;
      }
    }
    this._setShown(realNextId);
  }

  _setShown(id) {
    if (this.shownId === id) return;
    this.shownId = id;
    // Re-bind gate to the new region so the bar tracks it. Idempotent
    // when nothing actually changed.
    this.gate.bindBar(this.barEl, this.shownId);
    const region =
      id != null ? this.regions.find((r) => r.id === id) ?? null : null;
    this.onTargetChange(region);
  }

  _startCelebration() {
    this.celebrating = true;
    this.onCelebrationStart();
    this.celebrationTimer = setTimeout(() => {
      this.celebrationTimer = null;
      this.celebrating = false;
      const next = this._pickNext();
      this._setShown(next?.id ?? null);
      this._emit();
      this.onCelebrationEnd();
    }, this.celebrationMs);
  }

  _onGateTransition() {
    this._maybeAdvanceShown();
    this._emit();
  }

  _emit() {
    for (const l of this.listeners) l();
  }
}
